import React from 'react';
import { Box } from '@mui/material';
import { AgronodTypography } from '@agronod/mui-components';
import { parseInfoText, type InfoTextListStyle } from '@/utils/parseInfoText';

interface FormattedInfoTextProps {
  text: string;
  /** Text size; body1 where the text is read at length, as in the phone sheet. */
  variant?: 'body1' | 'body2';
}

const LIST_STYLE_TYPE: Record<InfoTextListStyle, string> = {
  bullet: 'disc',
  number: 'decimal',
  letter: 'lower-alpha',
};

/**
 * Renders admin-authored help text as paragraphs and lists.
 *
 * The syntax is line-based and documented on `parseInfoText`: `- ` for bullets,
 * `1. ` for numbered and `a. ` for letter lists, blank line for a new paragraph.
 * Line breaks inside a paragraph are kept.
 */
const FormattedInfoText: React.FC<FormattedInfoTextProps> = ({ text, variant = 'body2' }) => {
  const blocks = parseInfoText(text);
  if (blocks.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {blocks.map((block, index) =>
        block.type === 'paragraph' ? (
          <AgronodTypography key={index} variant={variant} sx={{ whiteSpace: 'pre-line' }}>
            {block.text}
          </AgronodTypography>
        ) : (
          <Box
            key={index}
            component={block.style === 'bullet' ? 'ul' : 'ol'}
            sx={{
              m: 0,
              pl: 2.5,
              listStyleType: LIST_STYLE_TYPE[block.style],
              '& li': { mb: 0.5, pl: 0.5 },
              '& li:last-of-type': { mb: 0 },
            }}
          >
            {block.items.map((item, itemIndex) => (
              <AgronodTypography component="li" variant={variant} key={itemIndex}>
                {item}
              </AgronodTypography>
            ))}
          </Box>
        ),
      )}
    </Box>
  );
};

export default FormattedInfoText;
